import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Hardcoded public key to avoid Vercel env mapping issues. This is perfectly safe as it is a public key.
const VAPID_PUBLIC_KEY = "BAW38Rfb6lDFe_wXggPH0wsHAIPXLE8rtRHEMVO_4QlCm837ExpTfaOi8Z3BKcHV8UeqJHX7TEVSL4OGHxzF4v8";

export function usePushNotifications() {
 const { user } = useAuth();
 const [subscription, setSubscription] = useState<PushSubscription | null>(null);
 const [permission, setPermission] = useState<NotificationPermission>(
 typeof Notification !== 'undefined' ? Notification.permission : 'default'
 );

 useEffect(() => {
 if ('serviceWorker' in navigator) {
 checkSubscription();
 }
 }, [user]);

 const checkSubscription = async () => {
 try {
 const registration = await navigator.serviceWorker.ready;
 const sub = await registration.pushManager.getSubscription();
 
 if (sub) {
   // Check if the subscription matches the CURRENT VAPID key
   const currentKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
   const subKey = sub.options.applicationServerKey;
   
   // Convert to hex for simple comparison
   const bufToHex = (buf: ArrayBuffer | null) => buf ? Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('') : '';
   
   if (bufToHex(subKey) !== bufToHex(currentKey)) {
     console.warn("VAPID Key mismatch! Unsubscribing old key and regenerating...");
     await sub.unsubscribe();
     const newSub = await registration.pushManager.subscribe({
       userVisibleOnly: true,
       applicationServerKey: currentKey,
     });
     setSubscription(newSub);
     await saveSubscriptionToDb(newSub);
     return;
   }

   setSubscription(sub);
   await saveSubscriptionToDb(sub);
 } else {
   // Permission is granted but no subscription exists (e.g. was deleted due to expiration or 410)
   // We should auto-subscribe silently to restore the subscription
   if (typeof Notification !== 'undefined' && Notification.permission === "granted" && VAPID_PUBLIC_KEY) {
     console.log("Permission granted but no subscription found. Auto-subscribing...");
     try {
       const newSub = await registration.pushManager.subscribe({
         userVisibleOnly: true,
         applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
       });
       setSubscription(newSub);
       await saveSubscriptionToDb(newSub);
     } catch (autoErr) {
       console.error("Failed to auto-subscribe:", autoErr);
     }
   }
 }
 } catch (error) {
 console.error('Error checking push subscription:', error);
 }
 };

 const urlBase64ToUint8Array = (base64String: string) => {
 const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
 const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
 const rawData = window.atob(base64);
 const outputArray = new Uint8Array(rawData.length);
 for (let i = 0; i < rawData.length; ++i) {
 outputArray[i] = rawData.charCodeAt(i);
 }
 return outputArray;
 };

 const subscribe = async () => {
 if (!VAPID_PUBLIC_KEY) {
 console.error('VAPID Public Key missing');
 return;
 }

 try {
 const result = await Notification.requestPermission();
 setPermission(result);
 
 if (result === 'granted') {
 const registration = await navigator.serviceWorker.ready;
 let sub;
 try {
   sub = await registration.pushManager.subscribe({
   userVisibleOnly: true,
   applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
   });
 } catch (subError) {
   console.warn("Failed to subscribe with new key. Attempting to unsubscribe old key first...", subError);
   const oldSub = await registration.pushManager.getSubscription();
   if (oldSub) {
     await oldSub.unsubscribe();
     sub = await registration.pushManager.subscribe({
       userVisibleOnly: true,
       applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
     });
   } else {
     throw subError;
   }
 }
 
 setSubscription(sub);
 await saveSubscriptionToDb(sub);
 toast.success('Notificações ativadas com sucesso!');
 } else {
 toast.error('Permissão de notificação negada.');
 }
 } catch (error: any) {
 console.error('Error subscribing to push:', error);
 toast.error(`Erro ao ativar: ${error.message || 'Desconhecido'}`);
 }
 };

 const saveSubscriptionToDb = async (sub: PushSubscription) => {
 try {
 const getPlatform = () => {
 const ua = navigator.userAgent.toLowerCase();
 if (ua.includes('iphone') || ua.includes('ipad') || (ua.includes('macintosh') && navigator.maxTouchPoints > 1)) return 'ios';
 if (ua.includes('android')) return 'android';
 return 'pc';
 };

 const platform = getPlatform();

 const { error } = await supabase
 .from('push_subscriptions' as any)
 .upsert({
 user_id: user?.id || null,
 subscription: sub.toJSON(),
 platform: platform,
 updated_at: new Date().toISOString()
 }, {
 onConflict: 'endpoint'
 });
 
 if (error) throw error;
 } catch (error) {
 console.error('Error saving subscription to DB:', error);
 }
 };

 return {
 permission,
 subscription,
 subscribe,
 isSupported: typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window
 };
}
